import React from 'react';

const MEDALS = {
  1: '/game/medals/medal-gold.png',
  2: '/game/medals/medal-silver.png',
  3: '/game/medals/medal-bronze.png',
};

const ALT = {
  1: 'Золотая медаль',
  2: 'Серебряная медаль',
  3: 'Бронзовая медаль',
};

const SIZE_PRESETS = { xs: 18, sm: 28, md: 44, lg: 72, xl: 128 };

/**
 * Place medal icon (gold/silver/bronze) for 1st/2nd/3rd places.
 *
 * Props:
 *   place      - 1 | 2 | 3 (required for visual; if omitted but bestAnswer=true
 *                shows a gold medal as visual carrier for the badge)
 *   size       - 'xs' | 'sm' | 'md' | 'lg' | 'xl' (preset, default 'md')
 *   height     - override height in px (takes precedence over `size`)
 *   bestAnswer - if true, overlay a "Лучший ответ" ribbon on top
 *   title      - tooltip text
 *   style      - extra inline styles (merged)
 *   className
 */
export default function PlaceMedal({
  place,
  size = 'md',
  height,
  bestAnswer = false,
  style,
  className,
  title,
}) {
  // Resolve the medal artwork: real place if present, otherwise gold carrier when bestAnswer is set.
  const visualPlace = (place === 1 || place === 2 || place === 3) ? place : (bestAnswer ? 1 : null);
  if (!visualPlace) return null;

  const h = height || SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const isCarrierOnly = !place && bestAnswer; // gold medal shown only as background for badge
  const computedTitle = title || (bestAnswer
    ? (place ? `${ALT[place]} + лучший ответ` : 'Лучший ответ (медаль уже занята другим игроком)')
    : ALT[visualPlace]);

  return (
    <span
      className={className}
      title={computedTitle}
      data-testid={place ? `medal-place-${place}` : 'medal-best-answer'}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'flex-start',
        verticalAlign: 'middle',
        lineHeight: 0,
        ...style,
      }}
    >
      <img
        src={MEDALS[visualPlace]}
        alt={ALT[visualPlace]}
        draggable={false}
        style={{
          height: `${h}px`,
          width: 'auto',
          display: 'block',
          userSelect: 'none',
          flexShrink: 0,
          // Slight desaturation when the medal is purely decorative (no real prize won).
          filter: isCarrierOnly ? 'saturate(0.85) brightness(0.95)' : undefined,
        }}
      />
      {bestAnswer && (
        <span
          data-testid="medal-best-answer-ribbon"
          style={{
            position: 'absolute',
            top: Math.max(4, Math.round(h * 0.04)),
            left: '50%',
            transform: 'translateX(-50%) rotate(-3deg)',
            background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 50%, #fbbf24 100%)',
            color: '#fff',
            fontSize: `${Math.max(8, Math.round(h * 0.115))}px`,
            fontWeight: 800,
            letterSpacing: '0.02em',
            padding: `${Math.max(1, Math.round(h * 0.02))}px ${Math.max(4, Math.round(h * 0.05))}px`,
            borderRadius: `${Math.max(2, Math.round(h * 0.04))}px`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
            textShadow: '0 1px 1px rgba(0,0,0,0.45)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 2,
            textTransform: 'uppercase',
            lineHeight: 1.1,
          }}
        >
          Лучший
        </span>
      )}
    </span>
  );
}
